import * as React from 'react'
import clsx from 'clsx'
import { SxProps, sx as sxFunc } from '../sx'
import style from './index.module.scss'
import { capitalize } from '../../utils'

export interface DividerClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `absolute={true}`. */
  absolute: string
  /** Styles applied to the root element if `variant="fullWidth"`. */
  fullWidth: string
  /** Styles applied to the root element if `light={true}`. */
  light: string
  /** Styles applied to the root element if `variant="middle"`. */
  middle: string
  /** Styles applied to the root element if `orientation="vertical"`. */
  vertical: string
  /** Styles applied to the root element if `flexItem={true}`. */
  flexItem: string
  /** Styles applied to the root element if divider have text. */
  withChildren: string
  /** Styles applied to the root element if divider have text and `orientation="vertical"`. */
  withChildrenVertical: string
  /** Styles applied to the root element if `textAlign="right" orientation="horizontal"`. */
  textAlignRight: string
  /** Styles applied to the root element if `textAlign="left" orientation="horizontal"`. */
  textAlignLeft: string
  /** Styles applied to the span children element if `orientation="horizontal"`. */
  wrapper: string
  /** Styles applied to the span children element if `orientation="vertical"`. */
  wrapperVertical: string
  /** Styles applied to the root element if `color="primary"`. */
  colorPrimary: string
  /** Styles applied to the root element if `color="secondary"`. */
  colorSecondary: string
  /** Styles applied to the root element if `color="error"`. */
  colorError: string
  /** Styles applied to the root element if `color="info"`. */
  colorInfo: string
  /** Styles applied to the root element if `color="success"`. */
  colorSuccess: string
  /** Styles applied to the root element if `color="warning"`. */
  colorWarning: string
}

export interface DividerProps extends React.HTMLAttributes<HTMLHRElement> {
  /**
   * Absolutely position the element.
   * @default false
   */
  absolute?: boolean
  /**
   * The content of the component.
   */
  children?: React.ReactNode
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default hr
   */
  Component?: React.ElementType
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<DividerClasses>
  /**
   * If `true`, a vertical divider will have the correct height when used in flex container.
   * (By default, a vertical divider will have a calculated height of `0px` if it is the child of a flex container.)
   * @default false
   */
  flexItem?: boolean
  /**
   * If `true`, the divider will have a lighter color.
   * @default false
   */
  light?: boolean
  /**
   * The component orientation.
   * @default 'horizontal'
   */
  orientation?: 'horizontal' | 'vertical'
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The text alignment.
   * @default 'center'
   */
  textAlign?: 'center' | 'right' | 'left'
  /**
   * The variant to use.
   * @default 'fullWidth'
   */
  variant?: 'fullWidth' | 'middle'
  /**
   * The color of the component.
   * It supports both default and custom theme colors, which can be added as shown in the
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
}

export const Divider = React.forwardRef<HTMLHRElement, DividerProps>(
  function Divider(props, ref) {
    const {
      absolute = false,
      children,
      className,
      Component = children ? 'div' : 'hr',
      flexItem = false,
      light = false,
      orientation = 'horizontal',
      role = Component !== 'hr' ? 'separator' : undefined,
      textAlign = 'center',
      variant = 'fullWidth',
      color = 'primary',
      classes,
      sx,
      ...other
    } = props

    return (
      <Component
        className={clsx(
          style.root,
          classes?.root,
          absolute && [style.absolute, classes?.absolute],
          light && [style.light, classes?.light],
          flexItem && [style.flexItem, classes?.flexItem],
          textAlign && [
            style[`textAlign-${textAlign}`],
            // @ts-ignore
            classes?.[`textAlign${capitalize(textAlign)}`],
          ],
          [
            style[`oritentation-${orientation}`],
            // @ts-ignore
            classes?.[orientation],
          ],
          [
            style[`variant-${variant}`],
            // @ts-ignore
            classes?.[variant],
          ],
          [
            style[`color-${color}`],
            // @ts-ignore
            classes?.[`color${capitalize(color)}`],
          ],
          children && [
            style.children,
            style[`oritentation-${orientation}`],
            orientation === 'vertical'
              ? classes?.withChildrenVertical
              : classes?.withChildren,
          ],
          className,
          sxFunc(sx)
        )}
        role={role}
        ref={ref}
        {...other}
      >
        {children ? (
          <span
            className={clsx(
              style.wrapper,
              orientation === 'horizontal'
                ? classes?.wrapper
                : classes?.wrapperVertical
            )}
          >
            {children}
          </span>
        ) : null}
      </Component>
    )
  }
)
